/**
 * The sound bank, as data.
 *
 * Every sound in the game is described here as a small stack of synthesizer
 * voices — no audio files, no decoding, no download weight. `synth.ts` knows how
 * to render one of these specs through Web Audio; `director.ts` knows which one
 * a given game event deserves. This module stays free of the DOM so the mapping
 * layer can be tested in Node.
 *
 * Tuning is meant to happen here and nowhere else.
 */

export type Cue =
  // Interface
  | 'ui.move'
  | 'ui.select'
  | 'ui.back'
  | 'ui.toggle'
  | 'ui.deny'
  // Piece handling
  | 'piece.move'
  | 'piece.rotate'
  | 'piece.bump'
  | 'piece.hold'
  | 'piece.softDrop'
  | 'piece.hardDrop'
  | 'piece.lock'
  // Clears
  | 'clear.single'
  | 'clear.double'
  | 'clear.triple'
  | 'clear.tetris'
  | 'clear.tspin'
  | 'clear.combo'
  | 'clear.backToBack'
  // Run lifecycle
  | 'game.start'
  | 'game.pause'
  | 'game.resume'
  | 'game.levelUp'
  | 'game.over'
  | 'game.topple'
  | 'balance.warn';

export type Wave = 'sine' | 'square' | 'sawtooth' | 'triangle' | 'noise';

export type FilterKind = 'lowpass' | 'highpass' | 'bandpass';

export interface VoiceFilter {
  kind: FilterKind;
  /** Cutoff or centre in Hz, scaled by the play pitch like an oscillator is. */
  freq: number;
  q?: number;
}

/** One scheduled tone or noise burst inside a cue. */
export interface Voice {
  wave: Wave;
  /** Starting frequency in Hz. Ignored for noise except through `filter`. */
  freq: number;
  /** Sweeps to this frequency across the voice's life. Defaults to `freq`. */
  toFreq?: number;
  /** Fade-in, seconds. */
  attack: number;
  /** Fade-out to silence, seconds. */
  decay: number;
  /** Peak linear gain, 0..1, before the cue and master gains apply. */
  gain: number;
  /** Start offset from the cue's own start, seconds. */
  delay?: number;
  detune?: number;
  filter?: VoiceFilter;
}

export interface CueSpec {
  voices: readonly Voice[];
  /** Multiplies every voice gain. Per-cue balance lives here. */
  gain?: number;
  /**
   * Retriggers closer together than this are dropped. Guards the sounds that
   * are driven by repeat rates (DAS at 33 ms, soft drop at high gravity) from
   * turning into a buzz saw.
   */
  minIntervalMs?: number;
}

/** Percussive click used across the interface, parameterised. */
function click(freq: number, toFreq: number, gain: number, decay = 0.06): Voice {
  return { wave: 'square', freq, toFreq, attack: 0.001, decay, gain };
}

/** Short filtered noise burst: impacts, scrapes and thuds. */
function burst(
  freq: number,
  gain: number,
  decay: number,
  filter: VoiceFilter,
  delay = 0,
): Voice {
  return { wave: 'noise', freq, attack: 0.001, decay, gain, delay, filter };
}

/** Rising arpeggio builder: the reward language for clears. */
function arpeggio(
  steps: readonly number[],
  wave: Wave,
  gain: number,
  step = 0.055,
  decay = 0.18,
): Voice[] {
  return steps.map((freq, index) => ({
    wave,
    freq,
    attack: 0.004,
    decay,
    gain,
    delay: index * step,
  }));
}

/**
 * Equal-tempered helper so the arpeggios stay in one key (A minor pentatonic
 * around A4) instead of drifting into arbitrary frequencies.
 */
export function note(semitonesFromA4: number): number {
  return 440 * 2 ** (semitonesFromA4 / 12);
}

const A3 = note(-12);
const C4 = note(3);
const D4 = note(5);
const E4 = note(7);
const G4 = note(10);
const A4 = note(0);
const C5 = note(15);
const D5 = note(17);
const E5 = note(19);
const G5 = note(22);
const A5 = note(12);

export const CUES: Record<Cue, CueSpec> = {
  // ------------------------------------------------------------- interface
  'ui.move': {
    voices: [click(520, 660, 0.18, 0.04)],
    minIntervalMs: 40,
  },
  'ui.select': {
    voices: [click(440, 880, 0.22, 0.07), { ...click(880, 1320, 0.12, 0.09), delay: 0.05 }],
  },
  'ui.back': {
    voices: [click(660, 300, 0.2, 0.09)],
  },
  'ui.toggle': {
    voices: [
      { wave: 'triangle', freq: 700, toFreq: 1100, attack: 0.002, decay: 0.08, gain: 0.16 },
      burst(2600, 0.06, 0.05, { kind: 'bandpass', freq: 2600, q: 1.2 }),
    ],
  },
  'ui.deny': {
    voices: [{ wave: 'square', freq: 200, toFreq: 120, attack: 0.002, decay: 0.12, gain: 0.16 }],
    minIntervalMs: 120,
  },

  // ---------------------------------------------------------- piece handling
  'piece.move': {
    voices: [
      { wave: 'square', freq: 300, toFreq: 240, attack: 0.001, decay: 0.035, gain: 0.1 },
      burst(1800, 0.05, 0.025, { kind: 'highpass', freq: 1800 }),
    ],
    minIntervalMs: 25,
  },
  'piece.rotate': {
    voices: [
      { wave: 'triangle', freq: 420, toFreq: 620, attack: 0.001, decay: 0.06, gain: 0.15 },
      burst(3200, 0.05, 0.03, { kind: 'bandpass', freq: 3200, q: 0.9 }),
    ],
    minIntervalMs: 30,
  },
  'piece.bump': {
    voices: [
      { wave: 'square', freq: 150, toFreq: 110, attack: 0.001, decay: 0.05, gain: 0.09 },
      burst(400, 0.05, 0.04, { kind: 'lowpass', freq: 500 }),
    ],
    minIntervalMs: 90,
  },
  'piece.hold': {
    voices: [
      { wave: 'sine', freq: 520, toFreq: 780, attack: 0.004, decay: 0.11, gain: 0.16 },
      { wave: 'sine', freq: 780, toFreq: 520, attack: 0.004, decay: 0.11, gain: 0.09, delay: 0.05 },
    ],
  },
  'piece.softDrop': {
    voices: [{ wave: 'triangle', freq: 240, toFreq: 200, attack: 0.001, decay: 0.03, gain: 0.06 }],
    minIntervalMs: 45,
  },
  'piece.hardDrop': {
    voices: [
      // Descending whoosh, then the impact.
      { wave: 'sawtooth', freq: 700, toFreq: 90, attack: 0.002, decay: 0.09, gain: 0.1 },
      burst(1200, 0.14, 0.06, { kind: 'lowpass', freq: 1400 }, 0.05),
      {
        wave: 'sine',
        freq: 110,
        toFreq: 55,
        attack: 0.002,
        decay: 0.16,
        gain: 0.26,
        delay: 0.05,
      },
    ],
  },
  'piece.lock': {
    voices: [
      { wave: 'square', freq: 190, toFreq: 130, attack: 0.001, decay: 0.06, gain: 0.12 },
      burst(900, 0.07, 0.05, { kind: 'lowpass', freq: 1100 }),
    ],
    minIntervalMs: 30,
  },

  // ------------------------------------------------------------------ clears
  'clear.single': {
    voices: [
      ...arpeggio([A4, C5], 'square', 0.13),
      burst(2400, 0.06, 0.14, { kind: 'bandpass', freq: 2400, q: 0.7 }),
    ],
  },
  'clear.double': {
    voices: [
      ...arpeggio([A4, C5, E5], 'square', 0.13),
      burst(2600, 0.07, 0.18, { kind: 'bandpass', freq: 2600, q: 0.7 }),
    ],
  },
  'clear.triple': {
    voices: [
      ...arpeggio([A4, C5, E5, G5], 'square', 0.13),
      burst(2800, 0.08, 0.22, { kind: 'bandpass', freq: 2800, q: 0.7 }),
    ],
  },
  'clear.tetris': {
    voices: [
      ...arpeggio([A4, C5, E5, G5, A5], 'sawtooth', 0.1, 0.05, 0.22),
      // A fifth underneath turns the top clear into a chord, not just a run.
      { wave: 'square', freq: E4, attack: 0.006, decay: 0.5, gain: 0.09, delay: 0.05 },
      { wave: 'square', freq: A3, attack: 0.006, decay: 0.6, gain: 0.1 },
      burst(3200, 0.1, 0.35, { kind: 'bandpass', freq: 3000, q: 0.5 }),
    ],
  },
  'clear.tspin': {
    voices: [
      { wave: 'sine', freq: D4, toFreq: D5, attack: 0.004, decay: 0.26, gain: 0.16 },
      { wave: 'sine', freq: G4, toFreq: G5, attack: 0.004, decay: 0.26, gain: 0.1, delay: 0.03 },
      burst(1800, 0.06, 0.3, { kind: 'bandpass', freq: 1800, q: 0.4 }),
    ],
  },
  'clear.combo': {
    // Pitch is scaled by the director, one step per combo link.
    voices: [{ wave: 'triangle', freq: C4, toFreq: G4, attack: 0.003, decay: 0.12, gain: 0.14 }],
  },
  'clear.backToBack': {
    voices: [
      { wave: 'triangle', freq: E5, attack: 0.003, decay: 0.1, gain: 0.1 },
      { wave: 'triangle', freq: A5, attack: 0.003, decay: 0.14, gain: 0.08, delay: 0.06 },
    ],
  },

  // --------------------------------------------------------- run lifecycle
  'game.start': {
    voices: arpeggio([A4, E5, A5], 'square', 0.14, 0.08, 0.16),
  },
  'game.pause': {
    voices: [
      { wave: 'sine', freq: G4, attack: 0.005, decay: 0.14, gain: 0.14 },
      { wave: 'sine', freq: D4, attack: 0.005, decay: 0.2, gain: 0.12, delay: 0.08 },
    ],
  },
  'game.resume': {
    voices: [
      { wave: 'sine', freq: D4, attack: 0.005, decay: 0.14, gain: 0.12 },
      { wave: 'sine', freq: G4, attack: 0.005, decay: 0.2, gain: 0.14, delay: 0.08 },
    ],
  },
  'game.levelUp': {
    voices: [
      ...arpeggio([C5, E5, G5, C5 * 2], 'square', 0.12, 0.07, 0.24),
      { wave: 'sawtooth', freq: C4, toFreq: C5, attack: 0.01, decay: 0.4, gain: 0.07 },
    ],
  },
  'game.over': {
    voices: [
      // Four steps down, slow, with the pitch sagging on every step.
      { wave: 'square', freq: A4, toFreq: G4, attack: 0.006, decay: 0.3, gain: 0.14 },
      { wave: 'square', freq: G4, toFreq: E4, attack: 0.006, decay: 0.3, gain: 0.14, delay: 0.18 },
      { wave: 'square', freq: E4, toFreq: D4, attack: 0.006, decay: 0.34, gain: 0.14, delay: 0.36 },
      { wave: 'square', freq: D4, toFreq: A3, attack: 0.006, decay: 0.7, gain: 0.16, delay: 0.54 },
      burst(600, 0.05, 0.9, { kind: 'lowpass', freq: 700 }, 0.54),
    ],
  },
  'game.topple': {
    voices: [
      // The stack goes over: a slide, then rubble.
      { wave: 'sawtooth', freq: 420, toFreq: 60, attack: 0.01, decay: 0.75, gain: 0.14 },
      burst(900, 0.12, 0.5, { kind: 'lowpass', freq: 900 }, 0.1),
      burst(500, 0.1, 0.7, { kind: 'lowpass', freq: 500 }, 0.35),
      { wave: 'sine', freq: 90, toFreq: 40, attack: 0.01, decay: 0.8, gain: 0.2, delay: 0.3 },
    ],
  },
  'balance.warn': {
    voices: [
      { wave: 'square', freq: 880, attack: 0.004, decay: 0.1, gain: 0.12 },
      { wave: 'square', freq: 880, attack: 0.004, decay: 0.14, gain: 0.12, delay: 0.16 },
    ],
    minIntervalMs: 900,
  },
};

/** Longest possible tail of a cue, seconds. Used to size scheduling budgets. */
export function cueDuration(cue: Cue): number {
  return CUES[cue].voices.reduce(
    (longest, voice) => Math.max(longest, (voice.delay ?? 0) + voice.attack + voice.decay),
    0,
  );
}
